/**
 * One bar of one part, as MusicXML.
 *
 * Two things here are decisions rather than transcription.
 *
 * The document's timeline is global; MusicXML's is per part, and repeats the metre, the
 * tempo and the rehearsal mark in every one of them. Signatures are written into every
 * part, because a reader that renders a single part has to know them, but **tempo marks
 * and rehearsal letters are written only into the first part**. They belong to the piece,
 * not to an instrument, and writing them everywhere would make the importer read the same
 * mark once per part and have to recognise the duplicates.
 *
 * A voice is written from the start of the bar, so every voice after the first is preceded
 * by a `<backup>` of exactly what the voice before it consumed. Getting that number wrong
 * by a tick silently shifts the rest of the bar.
 */

import { ticksOf } from "../document/duration";
import type { EventId } from "../document/ids";
import { capacityOf, occupiedTicks } from "../document/query";
import type {
  Direction,
  GlobalMeasure,
  Part,
  PartMeasure,
  Score,
  WordsStyle,
} from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { BAR_STYLES, soundTempo, toDivisions } from "./common";
import { heldDirection } from "./export-held";
import { jumpsAfter, jumpsBefore } from "./export-jumps";
import { figurePlan } from "./export-figures";
import type { InstrumentPlan } from "./export-instruments";
import { harmonyPlan } from "./export-harmony";
import { attributesNode } from "./export-attributes";
import { printNode } from "./export-print";
import { silentOssiaNode, voiceOrder } from "./export-ossia";
import type { StaffMap } from "./export-ossia";
import { noteNodes } from "./export-notes";
import type { PartPlan } from "./export-plan";

/** Everything a bar needs that is the same for every bar of the export. */
export interface ExportContext {
  readonly score: Score;
  readonly part: Part;
  readonly plan: PartPlan;
  readonly divisions: number;
  /**
   * What number each of the part's staves is written as, the ossia staves counted in.
   *
   * Built once per part in `export-ossia.ts`, because an ossia is a staff of the part in
   * the file and shifts the numbering of everything under it.
   */
  readonly staves: StaffMap;
  /** Tempo and rehearsal marks are written once, into the first part only. */
  readonly leading: boolean;
  /**
   * Write each event's and bar's own id as an `id` attribute, for the renderer to carry.
   *
   * Bar ids go into the leading part only. One bar of the document is one `<measure>` per
   * part in the file, and an XML id may appear once in a document; a renderer that folds the
   * parts back into one system -- which is what a renderer does -- needs the bar named once.
   */
  readonly ids: boolean;
  /** The part's own id in the file, which a drum's instrument is written inside. */
  readonly partId: string;
  /** Every instrument the part is played on, and the bars where it takes one up. */
  readonly instruments: InstrumentPlan;
  /**
   * Runs of silent bars printed as one, by the bar each begins at.
   *
   * Empty in a score, which prints its bars one by one. The bars of a run are still all
   * written: MusicXML states the count at the head of the run and leaves the rest of the
   * bars in the file, so that the music is unchanged and only its drawing gathers.
   */
  readonly restRuns: ReadonlyMap<number, number>;
}

/**
 * The bar where the player picks another instrument up: its name printed over the staff,
 * and the sound the rest of the part is played with.
 *
 * Both halves are one `<direction>`, which is how the format says it and how the importer
 * reads it back: the words name the instrument to the player, the `<sound>` names it to
 * whatever is playing the file. The id is the one `<part-list>` declared for it.
 */
function instrumentChangeNode(context: ExportContext, index: number): XmlNode | null {
  const played = context.instruments.changes.get(index);
  if (played === undefined) return null;
  return el("direction", { placement: "above" }, [
    el("direction-type", {}, [leaf("words", played.name)]),
    el("sound", {}, [
      el("instrument-change", { id: played.id }, [
        played.sound === null ? null : leaf("instrument-sound", played.sound),
      ]),
      played.midiProgram === null
        ? null
        : el("midi-instrument", { id: played.id }, [leaf("midi-program", played.midiProgram + 1)]),
    ]),
  ]);
}

/** The tempo mark, rehearsal letter and signs of a bar, written only into the leading part. */
export function globalDirections(context: ExportContext, global: GlobalMeasure): XmlNode[] {
  if (!context.leading) return [];
  const nodes: XmlNode[] = [...jumpsBefore(global)];

  if (global.rehearsal !== null) {
    nodes.push(
      el("direction", { placement: "above" }, [
        el("direction-type", {}, [leaf("rehearsal", global.rehearsal)]),
      ]),
    );
  }

  const tempo = global.tempo;
  if (tempo !== null) {
    nodes.push(
      el("direction", { placement: "above" }, [
        tempo.text === null
          ? null
          : el("direction-type", {}, [leaf("words", tempo.text, WORDS_FONT.tempo)]),
        el("direction-type", {}, [
          el("metronome", {}, [
            leaf("beat-unit", tempo.unit.type),
            ...Array.from({ length: tempo.unit.dots }, () => el("beat-unit-dot")),
            leaf("per-minute", tempo.bpm),
          ]),
        ]),
        el("sound", { tempo: soundTempo(tempo.bpm, ticksOf(tempo.unit)) }),
      ]),
    );
  }

  return nodes;
}

/**
 * The font a text style is written in.
 *
 * MusicXML has no word for what a piece of text *means*, only for how it is drawn, so the
 * meaning has to travel as the drawing -- and it does travel, because every program that
 * writes these files draws the three the same way: an expression leans, a tempo is heavy,
 * and a technique is neither. Reading is the mirror of this, in `import-bar.ts`.
 */
const WORDS_FONT: Readonly<Record<WordsStyle, XmlNode["attrs"]>> = {
  expression: { "font-style": "italic" },
  technique: {},
  tempo: { "font-weight": "bold" },
};

function directionNode(direction: Direction): XmlNode {
  const content = direction.content;
  const type =
    content.kind === "dynamic"
      ? el("dynamics", {}, [el(content.mark)])
      : leaf("words", content.text, WORDS_FONT[content.style]);
  return el("direction", { placement: direction.placement }, [el("direction-type", {}, [type])]);
}

/** Everything written immediately before one note: the lines it opens, its dynamics and words. */
function beforeEvent(
  context: ExportContext,
  id: EventId,
  byAnchor: ReadonlyMap<EventId, readonly Direction[]>,
): XmlNode[] {
  const nodes: XmlNode[] = [];
  for (const held of context.plan.heldStarts.get(id) ?? []) {
    nodes.push(heldDirection(held, "start"));
  }
  for (const direction of byAnchor.get(id) ?? []) nodes.push(directionNode(direction));
  return nodes;
}

/**
 * The line ends written immediately after one note.
 *
 * A hairpin, a pedal or an octave sign ends where the note it ends on stops sounding, so its
 * stop goes after that note and not before it. Writing it before would place the end of a
 * line drawn over one long note ahead of its own beginning.
 */
function afterEvent(context: ExportContext, id: EventId): XmlNode[] {
  return (context.plan.heldStops.get(id) ?? []).map((held) => heldDirection(held, "stop"));
}

function anchored(measure: PartMeasure): ReadonlyMap<EventId, readonly Direction[]> {
  const byAnchor = new Map<EventId, Direction[]>();
  for (const direction of measure.directions) {
    const existing = byAnchor.get(direction.anchor);
    if (existing === undefined) byAnchor.set(direction.anchor, [direction]);
    else existing.push(direction);
  }
  return byAnchor;
}

/** The notes of a bar, one voice after another with a `<backup>` between them. */
function voiceNodes(context: ExportContext, measure: PartMeasure, capacity: number): XmlNode[] {
  const staves = context.staves;
  const numbered = staves.count > 1;
  const byAnchor = anchored(measure);
  // The order the lines are written in is not the order the bar holds them: an ossia is
  // written above the staff it stands over, and a bar that offers none still says so. See
  // `export-ossia.ts`.
  const order = voiceOrder(measure, staves);
  // Chord symbols and figures hang on the played music, not on the alternative above it.
  const at = order.findIndex((entry) => entry.voice !== null && !entry.voice.ossia);
  const timeline = order[at]?.voice ?? null;
  const harmonies = harmonyPlan(measure, timeline, context.divisions, capacity);
  const figures = figurePlan(measure, timeline, context.divisions, capacity);
  const seen = new Set<EventId>();
  const nodes: XmlNode[] = [];

  order.forEach((write, index) => {
    const { voice, number } = write;
    const ossia = voice === null || voice.ossia;
    let consumed = 0;
    if (voice === null) {
      nodes.push(silentOssiaNode(write, context.divisions, capacity, numbered));
      consumed = capacity;
    }
    for (const event of voice?.events ?? []) {
      // Chord symbols and figures come from the played timeline, which is the one the
      // reader's cursor is on here; the voices after it have already been backed up over.
      // Figures follow the harmony so that a bar carrying both is read in the order it is
      // drawn: the chord above the staff, then the figure below it.
      if (index === at) {
        nodes.push(...(harmonies.byEvent.get(event.id) ?? []));
        nodes.push(...(figures.byEvent.get(event.id) ?? []));
      }
      nodes.push(...beforeEvent(context, event.id, byAnchor));
      seen.add(event.id);
      nodes.push(
        ...noteNodes(event, {
          divisions: context.divisions,
          measureTicks: capacity,
          voice: number,
          staff: numbered ? write.staff : null,
          // An ossia stands on a staff of its own, and that staff is the ossia's alone:
          // there is no second staff of it for a note to be drawn across onto.
          staves: ossia ? [] : staves.played,
          slurs: context.plan.slurs.get(event.id) ?? [],
          glissandos: context.plan.glissandos.get(event.id) ?? [],
          tuplet: context.plan.tuplets.get(event.id) ?? null,
          accidentals: context.plan.accidentals.get(event.id) ?? [],
          beams: context.plan.beams.get(event.id) ?? [],
          id: context.ids ? event.id : null,
          kit: context.part.instrument.percussion,
          partId: context.partId,
        }),
      );
      nodes.push(...afterEvent(context, event.id));
      consumed += occupiedTicks(event, capacity);
    }
    const last = index === order.length - 1;
    if (!last && consumed > 0) {
      nodes.push(el("backup", {}, [leaf("duration", toDivisions(consumed, context.divisions))]));
    }
  });

  // A direction whose note has been deleted would otherwise vanish without a trace.
  const orphans = measure.directions.filter((direction) => !seen.has(direction.anchor));
  return [...harmonies.leading, ...figures.leading, ...orphans.map(directionNode), ...nodes];
}

/** The `<barline>` closing a bar, and the repeat or ending opening it. */
function barlineNodes(score: Score, index: number, side: "left" | "right"): XmlNode | null {
  const global = score.measures[index];
  if (global === undefined) return null;
  const repeat = global.repeat;
  const here = repeat?.endings ?? [];
  const neighbour = score.measures[side === "left" ? index - 1 : index + 1]?.repeat?.endings ?? [];
  const boundary = here.length > 0 && here.join(",") !== neighbour.join(",");

  const children =
    side === "left"
      ? [
          repeat?.start === true ? leaf("bar-style", "heavy-light") : null,
          boundary ? el("ending", { number: here.join(","), type: "start" }) : null,
          repeat?.start === true ? el("repeat", { direction: "forward" }) : null,
        ]
      : [
          global.barline === "regular" && repeat?.end === true
            ? leaf("bar-style", "light-heavy")
            : barStyle(global),
          boundary ? el("ending", { number: here.join(","), type: "stop" }) : null,
          repeat?.end === true
            ? el("repeat", { direction: "backward", times: repeat.times })
            : null,
        ];

  const kept = children.filter((child): child is XmlNode => child !== null);
  return kept.length === 0 ? null : el("barline", { location: side }, kept);
}

function barStyle(global: GlobalMeasure): XmlNode | null {
  const spelling = BAR_STYLES[global.barline];
  return spelling === null ? null : leaf("bar-style", spelling);
}

/**
 * The number a bar carries, which is not always its place in the list.
 *
 * A pickup at the head of the piece is bar 0 and the first full bar is bar 1, which is how
 * a musician counts and what a reader expects behind `implicit="yes"`. A short bar anywhere
 * else -- the one that closes a repeat begun mid-bar -- does not move the count.
 */
function barNumber(score: Score, index: number): number {
  const first = score.measures[0];
  const pickup = first !== undefined && first.length !== null ? 1 : 0;
  return index + 1 - pickup;
}

/** One `<measure>` of one part. */
export function measureNode(context: ExportContext, index: number): XmlNode | null {
  const global = context.score.measures[index];
  if (global === undefined) return null;
  const measure = context.part.measures[global.id];
  if (measure === undefined) return null;
  const capacity = capacityOf(context.score, index);

  const id = context.ids && context.leading ? global.id : null;
  // A bar shorter than its metre has to say so, or a reader counts the missing beats as an
  // error and either pads the bar out or refuses the file.
  const implicit = global.length === null ? null : "yes";
  return el("measure", { number: barNumber(context.score, index), implicit, id }, [
    printNode(context.score, index, global),
    barlineNodes(context.score, index, "left"),
    attributesNode(context, index),
    instrumentChangeNode(context, index),
    ...globalDirections(context, global),
    ...voiceNodes(context, measure, capacity),
    ...(context.leading ? jumpsAfter(context.score, global) : []),
    barlineNodes(context.score, index, "right"),
  ]);
}
